import { Trans, useTranslation } from "react-i18next";

// Илюстрацията в „Кухненски билети" — какво излиза на POS принтера. Продуктите
// в билета са примерни; станциите, името на принтера и другият принтер в
// обяснението са истинските.

const PAPER_CLASS =
  "flex flex-col rounded-[10px] border border-shade-3 bg-surface font-mono text-ink";

// „Кухненски билети": билетът на избрания език + обяснение с два принтера.
// Колко станции се изброяват по име, преди „и още N".
const LISTED_STATIONS_COUNT = 2;

export const PosKitchenTicketExample = ({
  languageCode,
  languageName,
  stationNames,
  printerName,
  printerStationNames,
  otherPrinter,
}: {
  languageCode: string;
  languageName: string;
  // Имената на избраните станции на езика на билета.
  stationNames: string[];
  // Името на този принтер и станциите му на езика на интерфейса.
  printerName: string;
  printerStationNames: string[];
  // Друг POS принтер със станции (ако има) — за примера с разделянето.
  otherPrinter: { name: string; stationNames: string[] } | null;
}) => {
  const { t, i18n } = useTranslation();
  // Текстът на билета е на езика на печата, не на интерфейса.
  const ticketT = i18n.getFixedT(languageCode);
  const sampleLines = [
    { qty: "2 ×", name: ticketT("devices.posModal.ticketItem1"), modifier: ticketT("devices.posModal.ticketMod1") },
    { qty: "1 ×", name: ticketT("devices.posModal.ticketItem2"), modifier: ticketT("devices.posModal.ticketMod2") },
  ].slice(0, Math.min(2, stationNames.length));

  return (
    <div className="grid grid-cols-[minmax(0,260px)_minmax(0,1fr)] items-center gap-3.5">
      <div className={`${PAPER_CLASS} gap-[3px] px-3 py-2.5 text-[11px] leading-[1.35]`}>
        <div className="flex justify-between text-[11px] font-semibold tracking-[0.6px]">
          <span>{ticketT("devices.posModal.ticketTitle")}</span>
          <span>#0418</span>
        </div>
        <div className="text-[12px] font-semibold">
          {stationNames
            .slice(0, 2)
            .map((stationName) => stationName.toUpperCase())
            .join(" · ") || "—"}
        </div>
        <div className="text-[10.5px] text-muted">{ticketT("devices.posModal.ticketMeta")}</div>
        <div className="my-0.5 border-t border-dashed border-shade-5" />
        {sampleLines.map((line) => (
          <div key={line.name} className="flex flex-col">
            <span>
              <b>{line.qty}</b> {line.name}
            </span>
            <span className="pl-7 text-muted">{line.modifier}</span>
          </div>
        ))}
        {stationNames.length === 0 && (
          <div className="font-sans text-[12px] text-muted">
            {t("devices.posModal.ticketEmpty")}
          </div>
        )}
      </div>
      {/* Заглавието на примера е вдясно, над обяснението — не заема цял ред. */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] font-bold leading-[1.3] text-ink">
            {t("devices.posModal.kitchenExampleTitle")}
          </span>
          <span className="rounded-full bg-fill-2 px-2 py-px text-[11px] font-semibold text-body">
            {languageName}
          </span>
        </div>
        <div className="text-[12px] leading-normal text-body [text-wrap:pretty]">
          <KitchenExampleExplanation
            printerName={printerName}
            printerStationNames={printerStationNames}
            otherPrinter={otherPrinter}
          />
        </div>
      </div>
    </div>
  );
};

// „Бар, Скара" / „Бар и Скара" / „Бар, Скара и още 3".
const useStationList = () => {
  const { t } = useTranslation();
  return (names: string[]) => {
    if (names.length <= 1) return names.join("");
    if (names.length === 2) return names.join(t("devices.posModal.exampleListAnd"));
    return t("devices.posModal.exampleListMore", {
      list: names.slice(0, LISTED_STATIONS_COUNT).join(", "),
      count: names.length - LISTED_STATIONS_COUNT,
    });
  };
};

// Обяснението до билета — от истинските данни: този принтер и станциите му,
// друг POS принтер със станции и как се разделя поръчката.
const KitchenExampleExplanation = ({
  printerName,
  printerStationNames,
  otherPrinter,
}: {
  printerName: string;
  printerStationNames: string[];
  otherPrinter: { name: string; stationNames: string[] } | null;
}) => {
  const { t } = useTranslation();
  const formatStations = useStationList();
  const trimmedName = printerName.trim();
  const printer = trimmedName
    ? t("devices.posModal.examplePrinterNamed", { name: trimmedName })
    : t("devices.posModal.exampleThisPrinter");
  const hasStations = printerStationNames.length > 0;
  return (
    <>
      {hasStations ? (
        <Trans
          i18nKey="devices.posModal.exampleListens"
          values={{ printer, stations: formatStations(printerStationNames) }}
          components={{ b: <b /> }}
        />
      ) : (
        t("devices.posModal.exampleNoStations", { printer })
      )}{" "}
      {otherPrinter && (
        <>
          <Trans
            i18nKey="devices.posModal.exampleListens"
            values={{
              printer: t("devices.posModal.examplePrinterNamed", { name: otherPrinter.name }),
              stations: formatStations(otherPrinter.stationNames),
            }}
            components={{ b: <b /> }}
          />{" "}
        </>
      )}
      {hasStations &&
        (otherPrinter
          ? t("devices.posModal.exampleSplit")
          : t("devices.posModal.exampleSingle"))}
    </>
  );
};
